import { companyStorageKey } from './companyStorage'
// Each document gets a fresh ID, including tabs cloned from an opener.
const tabId = crypto.randomUUID()
const ACTIVE_TAB_PREFIX = 'salesRepApp_activeTabs_'
const HEARTBEAT_INTERVAL_MS = 15000
const STALE_TAB_MS = 45000

let trackedToken: string | null = null
let heartbeatTimer: number | null = null

function getTabId(): string { return tabId }

function registryKey(token: string): string {
  return companyStorageKey(ACTIVE_TAB_PREFIX + token)
}

function readRegistry(token: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(registryKey(token))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, number>
    return typeof parsed === 'object' && parsed != null ? parsed : {}
  } catch {
    return {}
  }
}

function writeRegistry(token: string, registry: Record<string, number>): void {
  const entries = Object.entries(registry)
  if (entries.length === 0) {
    localStorage.removeItem(registryKey(token))
    return
  }

  localStorage.setItem(registryKey(token), JSON.stringify(Object.fromEntries(entries)))
}

function pruneRegistry(registry: Record<string, number>): Record<string, number> {
  const now = Date.now()
  return Object.fromEntries(
    Object.entries(registry).filter(([, timestamp]) => now - timestamp < STALE_TAB_MS)
  )
}

function touchTrackedTab(token: string): void {
  const tabId = getTabId()
  const registry = pruneRegistry(readRegistry(token))
  registry[tabId] = Date.now()
  writeRegistry(token, registry)
}

function clearHeartbeat(): void {
  if (heartbeatTimer != null) {
    window.clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

export function startSessionTabTracking(token: string): void {
  if (!token) return

  if (trackedToken && trackedToken !== token) {
    stopSessionTabTracking(trackedToken)
  }

  trackedToken = token
  touchTrackedTab(token)

  clearHeartbeat()
  heartbeatTimer = window.setInterval(() => {
    if (!trackedToken) return
    touchTrackedTab(trackedToken)
  }, HEARTBEAT_INTERVAL_MS)
}

export function stopSessionTabTracking(token?: string): boolean {
  const targetToken = token ?? trackedToken
  if (!targetToken) return true

  if (trackedToken === targetToken) {
    clearHeartbeat()
    trackedToken = null
  }

  const tabId = getTabId()
  const registry = pruneRegistry(readRegistry(targetToken))
  delete registry[tabId]
  writeRegistry(targetToken, registry)

  return Object.keys(registry).length === 0
}
